import { motion } from "framer-motion";
import { Compass, Rocket } from "lucide-react";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1712111878737-c1d644970d1a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjB0ZWNobm9sb2d5JTIwZGFyayUyMGFlc3RoZXRpY3xlbnwwfHx8fDE3NzczMDgyMDB8MA&ixlib=rb-4.1.0&q=85";

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 sm:py-32 px-5 sm:px-8 max-w-7xl mx-auto"
    >
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-7"
        >
          <div className="text-xs uppercase tracking-[0.3em] text-[#FF6B00]">
            About AnimaSoftware
          </div>
          <h2
            className="mt-5 text-3xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tighter"
            data-testid="about-headline"
          >
            We engineer the&nbsp;
            <span className="text-gradient">third dimension</span> for ambitious
            brands.
          </h2>

          <p className="mt-7 text-base sm:text-lg text-gray-300 leading-relaxed max-w-2xl">
            Based in Belo Horizonte, MG, Brazil, AnimaSoftware is
            a studio of engineers, technical artists and product designers
            obsessed with one thing: shipping 3D software that performs. From
            custom real-time engines to industrial visualization and immersive
            VR/AR products, we turn complex ideas into precise, beautiful, and
            scalable digital environments.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-5">
            <div
              className="group relative rounded-2xl border border-white/10 bg-white/[0.03] p-6 hover:border-[#FF6B00]/40 hover:bg-white/[0.05] transition-all duration-300"
              data-testid="about-mission-card"
            >
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#FF6B00]/15 text-[#FF6B00] mb-4">
                <Rocket className="h-5 w-5" />
              </div>
              <h3 className="font-[Unbounded] text-lg font-semibold mb-2">
                Our Mission
              </h3>
              <p className="text-sm text-gray-400 leading-relaxed">
                Deliver immersive 3D solutions that move ideas from concept to
                interactive reality — fast, robust, and beautifully optimized.
              </p>
            </div>

            <div
              className="group relative rounded-2xl border border-white/10 bg-white/[0.03] p-6 hover:border-[#00FF66]/40 hover:bg-white/[0.05] transition-all duration-300"
              data-testid="about-vision-card"
            >
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#00FF66]/15 text-[#00FF66] mb-4">
                <Compass className="h-5 w-5" />
              </div>
              <h3 className="font-[Unbounded] text-lg font-semibold mb-2">
                Our Vision
              </h3>
              <p className="text-sm text-gray-400 leading-relaxed">
                Shape the future of digital interaction by making real-time 3D,
                XR and spatial computing the default language of software.
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5 relative"
          data-testid="about-image-block"
        >
          <div className="absolute -inset-6 rounded-[28px] bg-gradient-to-br from-[#FF6B00]/20 to-[#00FF66]/20 blur-2xl" />
          <div className="relative rounded-3xl overflow-hidden border border-white/10">
            <img
              src={ABOUT_IMG}
              alt="AnimaSoftware studio"
              className="w-full h-[460px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-gray-300">
                  Studio HQ
                </div>
                <div className="font-[Unbounded] text-lg font-semibold">
                  Belo Horizonte, MG, Brazil
                </div>
              </div>
              <div className="hidden sm:block h-10 w-10 rounded-full border border-[#00FF66]/40 bg-black/40 backdrop-blur grid place-items-center">
                <span className="block h-2 w-2 rounded-full bg-[#00FF66] animate-pulse" />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
